import { computed, inject, Injectable, signal } from '@angular/core';
import { AuditCategoryEnum, AuditController, AuditLogEntity } from '@domain';
import { AuditFilterParams } from 'src/app/domain/DTOs/audit-filter.dto';
import { PaginatedEntity, PaginationParams } from 'src/app/domain/DTOs/paginate.entity';
import { EditDataService } from './_edit-data.service';

/**
 * Estado da timeline de histórico do documento aberto no editor. Busca a
 * auditoria escopada ao `documentId` atual (endpoint liberado para quem tem
 * acesso ao documento). Padrão "carregar mais" (acumula eventos).
 *
 * Fornecido no componente do painel (não root) para não colidir com a tela de
 * auditoria admin.
 */
@Injectable()
export class DocumentHistoryService {
    public loading = signal<boolean>(false);
    public events = signal<AuditLogEntity[]>([]);
    public paginate = signal<PaginatedEntity<AuditLogEntity>>(null);
    public category = signal<AuditCategoryEnum | null>(null);

    /** Ainda há páginas para carregar. */
    public hasMore = computed<boolean>(() => {
        const p = this.paginate();
        return !!p && p.page < p.totalPages;
    });

    private _editData = inject(EditDataService);
    private _auditController = inject(AuditController);

    /**
     * Carrega uma página do histórico.
     * @param page Página (default 1)
     * @param append Quando true, acrescenta ao final (carregar mais); senão, substitui.
     */
    public load(page: number = 1, append: boolean = false): void {
        const documentId = this._editData.document()?._id;
        if (!documentId) return;

        this.loading.set(true);

        const paginate: PaginationParams = { page, limit: 15 };
        const filters: AuditFilterParams = {
            category: this.category() ?? undefined,
        };

        this._auditController.getDocumentHistory(documentId, paginate, filters).subscribe({
            next: result => {
                this.paginate.set(result);
                const data = result?.data ?? [];
                this.events.update(prev => (append ? [...prev, ...data] : data));
                this.loading.set(false);
            },
            error: () => this.loading.set(false),
        });
    }

    /** Carrega a próxima página, acrescentando ao final. */
    public loadMore(): void {
        const p = this.paginate();
        if (p && p.page < p.totalPages) this.load(p.page + 1, true);
    }

    /** Aplica um filtro de categoria (ou `null` para todas) e recarrega do início. */
    public setCategory(category: AuditCategoryEnum | null): void {
        this.category.set(category);
        this.load(1, false);
    }
}
